import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Tabs,
  Tab,
  TextField,
  Button,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Grid,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip

} from '@mui/material';
import { CloudUpload, Download } from '@mui/icons-material';
import { toast } from 'react-toastify';
import Layout from '../../components/Layout';
import { verificationAPI, pricingAPI } from '../../services/api';

const KYCVerification = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [serviceType, setServiceType] = useState('');
  const [formData, setFormData] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [error, setError] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [bulkResults, setBulkResults] = useState([]);
  const [certProgress, setCertProgressState] = useState(() => {
    try {
      const saved = localStorage.getItem('certProgress');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });

  const setCertProgress = (val) => {
    setCertProgressState(prev => {
      const newVal = typeof val === 'function' ? val(prev) : val;
      if (newVal) {
        localStorage.setItem('certProgress', JSON.stringify(newVal));
      } else {
        localStorage.removeItem('certProgress');
      }
      return newVal;
    });
  };
  const [uploadLoading, setUploadLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const fileInputRef = useRef(null);
  const failureCountRef = useRef(0);
  const pollingIntervalRef = useRef(null);
  
  // GSTIN Filing Details State
  const [showFilingTable, setShowFilingTable] = useState(false);
  const [showFrequencyTable, setShowFrequencyTable] = useState(false);
  const [selectedFY, setSelectedFY] = useState('');
  const [filingYearData, setFilingYearData] = useState(null);
  const [frequencyPeriod, setFrequencyPeriod] = useState('current'); // 'current' or 'previous'
  
  // Cancel dialog state
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [jobToCancel, setJobToCancel] = useState(null);
  
  // Tab switch warning dialog state
  const [tabSwitchDialogOpen, setTabSwitchDialogOpen] = useState(false);
  const [pendingTabChange, setPendingTabChange] = useState(null);

  useEffect(() => {
    fetchServiceTypes();
    fetchBulkJobs();
    
    // Set up polling interval with circuit breaker
    pollingIntervalRef.current = setInterval(() => {
      // Stop polling if too many failures (circuit breaker)
      if (failureCountRef.current < 5) {
        fetchBulkJobs();
      } else {
        // console.warn('⚠️ Polling stopped due to repeated failures. Refresh page to retry.');
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
        }
      }
    }, 3000); // Poll every 3 seconds for real-time progress updates
    
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  const fetchServiceTypes = async () => {
    try {
      setLoadingTypes(true);
      const response = await pricingAPI.getAll();
      if (response.success) {
        setServiceTypes(response.data);
        if (response.data.length > 0) {
          setServiceType(response.data[0].name);
        }
      }
    } catch (error) {
      // console.error('Error fetching service types:', error);
      toast.error('Failed to load service types');
    } finally {
      setLoadingTypes(false);
    }
  };

  const fetchBulkJobs = async () => {
    try {
      const response = await verificationAPI.getBulkJobs();
      if (response.success) {
        setBulkResults(response.data);
        // Reset failure count on success
        failureCountRef.current = 0;
        
        // Log any status changes for debugging
        const processingJobs = response.data.filter(job => job.status === 'processing' || job.status === 'pending');
        if (processingJobs.length > 0) {
        }
      }
    } catch (error) {
      // Increment failure count
      failureCountRef.current += 1;
      
      // Only log error once to avoid console spam
      if (!window._bulkJobsFetchErrorLogged) {
        // console.error('Error fetching bulk jobs:', error);
        window._bulkJobsFetchErrorLogged = true;
        
        // Reset error flag after 30 seconds
        setTimeout(() => {
          window._bulkJobsFetchErrorLogged = false;
        }, 30000);
      }
    }
  };

  const handleSingleVerification = async (e) => {
    e.preventDefault();
    
    // Prevent multiple submissions
    if (loading) {
      return;
    }
    
    setLoading(true);
    setError('');
    setResult(null);
    setShowFilingTable(false);
    setShowFrequencyTable(false);
    setSelectedFY('');
    setFilingYearData(null);
    setFrequencyPeriod('current');

    try {
      let response;
      
      // Check service type and call appropriate API
      if (serviceType.toLowerCase().includes('bank')) {
        response = await verificationAPI.verifyBankAccount(
          formData.accountNumber,
          formData.ifsc?.toUpperCase(),
          formData.name
        );
      } else if (serviceType.toLowerCase().includes('cin') && serviceType.toLowerCase().includes('pan')) {
        const panValue = (formData.pan || formData.panNumber || formData.default || '').toUpperCase();
        response = await verificationAPI.verifyPANToCIN(panValue);
      } else if (serviceType.toLowerCase().includes('cin')) {
        response = await verificationAPI.verifyCIN(formData.cin?.toUpperCase());
      } else if (serviceType.toLowerCase().includes('driving license')) {
        response = await verificationAPI.verifyDrivingLicense(
          formData.dlNumber?.toUpperCase(),
          formData.dob
        );
      } else if (serviceType.toLowerCase().includes('voter')) {
        response = await verificationAPI.verifyVoterID(formData.epicNumber?.toUpperCase());
      } else if (serviceType.toLowerCase().includes('vehicle') || serviceType.toLowerCase().includes('rc')) {
        response = await verificationAPI.verifyVehicleRC(formData.regNo?.toUpperCase());
      } else if (serviceType.toLowerCase().includes('passport')) {
        response = await verificationAPI.verifyPassport(
          formData.fileNumber?.toUpperCase(),
          formData.dob
        );
      } else if (serviceType.toLowerCase().includes('pan to udyam') && serviceType.toLowerCase().includes('lite')) {
        const panValue = (formData.pan || formData.panNumber || formData.default || '').toUpperCase();
        response = await verificationAPI.verifyPANToUdyamLite(panValue);
      } else if (serviceType.toLowerCase().includes('pan to udyam')) {
        const panValue = (formData.pan || formData.panNumber || formData.default || '').toUpperCase();
        response = await verificationAPI.verifyPANToUdyam(panValue);
      } else if (serviceType.toLowerCase().includes('pan to gstin')) {
        response = await verificationAPI.searchGSTINByPAN(formData.pan?.toUpperCase());
      } else if (serviceType.toLowerCase().includes('pan to tan')) {
        response = await verificationAPI.verifyPANToTAN(formData.pan?.toUpperCase());
      } else if (serviceType.toLowerCase().includes('tan')) {
        response = await verificationAPI.verifyTAN(formData.tan?.toUpperCase());
      } else if (serviceType.toLowerCase().includes('gst lite') || serviceType.toLowerCase().includes('gst_lite')) {
        response = await verificationAPI.verifyGSTINLite(formData.gstin?.toUpperCase());
      } else if (serviceType.toLowerCase().includes('gstin') || serviceType.toLowerCase().includes('gst')) {
        response = await verificationAPI.verifyGSTIN(formData.gstin?.toUpperCase());
      } else if (serviceType.toLowerCase().includes('cibil')) {
        const panValue = (formData.pan || formData.panNumber || formData.default || '').toUpperCase();
        response = await verificationAPI.verifyCIBIL(panValue);
      } else if (serviceType.toLowerCase().includes('employment 360') || serviceType.toLowerCase().includes('employment360')) {
        const uanValue = (formData.uan || formData.uanNumber || formData.default || '').trim();
        response = await verificationAPI.verifyEmployment360(uanValue);
      } else if (serviceType.toLowerCase().includes('msme')) {
        response = await verificationAPI.verifyMSME(formData.urn?.toUpperCase());
      } else if (serviceType.toLowerCase().includes('udyam certificate') || serviceType.toLowerCase().includes('udyam cert')) {
        const udyamValue = (formData.udyamNumber || formData.urn || formData.default || '').toUpperCase();
        response = await verificationAPI.downloadUdyamCertificate(udyamValue);
      } else if (serviceType.toLowerCase().includes('udyam')) {
        response = await verificationAPI.verifyUdyam(formData.udyamNumber?.toUpperCase());
      } else if (serviceType.toLowerCase().includes('pan 360') || serviceType.toLowerCase().includes('pan360')) {
        const panValue = (formData.pan || formData.panNumber || formData.default || '').toUpperCase();
        response = await verificationAPI.verifyPAN360(panValue);
      } else if (serviceType.toLowerCase().includes('pan')) {
        response = await verificationAPI.verifyPAN(formData.pan?.toUpperCase());
      } else {
        setError(`Service type "${serviceType}" is not yet implemented`);
        setLoading(false);
        return;
      }
      
      if (response) {
        setResult(response);
        toast.success('Verification successful!');
      }
    } catch (err) {
      // console.error('❌ Verification failed:', err);
      const errorMessage = err.response?.data?.message || 'Verification failed';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  // Helper function to get unique financial years from filing data
  const getFinancialYears = () => {
    if (!result?.data?.filing_details) return [];
    
    const years = new Set();
    result.data.filing_details.gstr3b?.forEach(f => years.add(f.financialYear));
    result.data.filing_details.gstr1?.forEach(f => years.add(f.financialYear));
    result.data.filing_details.gstr9?.forEach(f => years.add(f.financialYear));
    result.data.filing_details.gstr9c?.forEach(f => years.add(f.financialYear));
    
    return Array.from(years).sort().reverse();
  };

  // Handle filing table search by year
  const handleFilingSearch = () => {
    if (!selectedFY || !result?.data?.filing_details) {
      return;
    }

    const filtered = {
      gstr3b: result.data.filing_details.gstr3b?.filter(f => f.financialYear === selectedFY) || [],
      gstr1: result.data.filing_details.gstr1?.filter(f => f.financialYear === selectedFY) || [],
      gstr9: result.data.filing_details.gstr9?.filter(f => f.financialYear === selectedFY) || [],
      gstr9c: result.data.filing_details.gstr9c?.filter(f => f.financialYear === selectedFY) || []
    };

    setFilingYearData(filtered);
  };

  // Get frequency data based on period selection
  const getFrequencyData = () => {
    if (!result?.data?.filing_frequency || result.data.filing_frequency.length === 0) return null;
    
    // If there's only one entry, return it
    if (result.data.filing_frequency.length === 1) {
      return result.data.filing_frequency[0];
    }
    
    // If there are multiple entries, try to find based on period
    // Sort by financial year descending
    const sorted = [...result.data.filing_frequency].sort((a, b) => {
      const yearA = parseInt(a.financialYear?.split('-')[0] || '0');
      const yearB = parseInt(b.financialYear?.split('-')[0] || '0');
      return yearB - yearA;
    });
    
    // Return first (most recent) for current, second for previous
    if (frequencyPeriod === 'current') {
      return sorted[0];
    } else {
      return sorted[1] || sorted[0]; // Fallback to first if no second entry
    }
  };

  const getSelectedServiceType = () => {
    return serviceTypes.find(s => s.name === serviceType);
  };

  const renderInputFields = () => {
    const selectedService = getSelectedServiceType();
    
    if (!selectedService || !selectedService.input_fields || selectedService.input_fields.length === 0) {
      // Fallback to single input with label
      return (
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label={selectedService?.input_label || 'Input Value'}
            value={formData.default || ''}
            onChange={(e) => handleInputChange('default', e.target.value)}
            placeholder={`Enter ${selectedService?.input_label || 'value'}`}
            required
          />
        </Grid>
      );
    }

    // Render fields from database configuration
    return selectedService.input_fields.map((field, index) => {
      // Convert input to uppercase for fields that need it (GSTIN, PAN, etc.)
      const shouldUppercase = field.name && (
        field.name.toLowerCase().includes('gstin') ||
        field.name.toLowerCase().includes('pan') ||
        field.name.toLowerCase().includes('tan') ||
        field.name.toLowerCase().includes('cin') ||
        field.name.toLowerCase().includes('ifsc') ||
        field.name.toLowerCase().includes('dl') ||
        field.name.toLowerCase().includes('license')
      );
      
      return (
        <Grid item xs={12} md={selectedService.input_fields.length > 2 ? 4 : 6} key={field.name}>
          <TextField
            fullWidth
            label={field.label}
            value={formData[field.name] || ''}
            onChange={(e) => handleInputChange(field.name, shouldUppercase ? e.target.value.toUpperCase() : e.target.value)}
            placeholder={field.placeholder}
            required={field.required}
            inputProps={{
              maxLength: field.maxLength,
              ...(field.pattern ? { pattern: field.pattern } : {}),
              title: field.label
            }}
            helperText={field.helperText}
          />
        </Grid>
      );
    });
  };

  const handleFileSelect = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const handleBulkUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    const selectedService = getSelectedServiceType();
    if (!selectedService) {
      toast.error('Please select a service type');
      return;
    }

    // Special handling for Udyam Certificate - download PDFs one by one
    if (selectedService.name.toLowerCase().includes('udyam certificate')) {
      setUploadLoading(true);
      setError('');
      try {
        let udyamNumbers = [];

        const fileExt = selectedFile.name.toLowerCase().split('.').pop();

        if (fileExt === 'xlsx' || fileExt === 'xls') {
          // Parse Excel
          const XLSX = await import('xlsx');
          const data = await selectedFile.arrayBuffer();
          const workbook = XLSX.read(data, { type: 'array' });
          const sheet = workbook.Sheets[workbook.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });
          // Skip header, get first column
          for (let i = 1; i < rows.length; i++) {
            const val = String(rows[i]?.[0] || '').trim().toUpperCase();
            if (val.startsWith('UDYAM')) udyamNumbers.push(val);
          }
        } else {
          // Parse CSV
          const text = await selectedFile.text();
          const lines = text.split('\n').map(l => l.trim()).filter(l => l);
          for (let i = 1; i < lines.length; i++) {
            const cols = lines[i].split(',').map(c => c.trim().replace(/"/g, ''));
            const num = (cols[0] || '').toUpperCase();
            if (num.startsWith('UDYAM')) udyamNumbers.push(num);
          }
        }

        if (udyamNumbers.length === 0) {
          toast.error('No valid Udyam numbers found in file');
          setUploadLoading(false);
          return;
        }

        toast.info(`Processing ${udyamNumbers.length} certificates...`);

        let successCount = 0;
        let failCount = 0;
        setCertProgress({ total: udyamNumbers.length, success: 0, failed: 0, current: '', status: 'processing', startedAt: new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }), completedAt: '' });

        for (let i = 0; i < udyamNumbers.length; i++) {
          try {
            setCertProgress(prev => ({ ...prev, current: udyamNumbers[i], success: successCount, failed: failCount }));
            await verificationAPI.downloadUdyamCertificate(udyamNumbers[i]);
            successCount++;
            // Wait between downloads
            await new Promise(resolve => setTimeout(resolve, 2000));
          } catch (err) {
            failCount++;
            toast.error(`Failed: ${udyamNumbers[i]}`, { autoClose: 3000 });
          }
        }

        setCertProgress(prev => ({ ...prev, success: successCount, failed: failCount, current: '', status: 'completed', completedAt: new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) }));
        toast.success(`Done! ${successCount} downloaded, ${failCount} failed.`);
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
      } catch (err) {
        toast.error('Failed to process file');
      } finally {
        setUploadLoading(false);
      }
      return;
    }

    setUploadLoading(true);
    setError('');

    try {
      const response = await verificationAPI.uploadBulkVerification(
        selectedFile,
        selectedService.id
      );

      if (response.success) {
        toast.success('Bulk verification job created successfully!');
        setSelectedFile(null);
        // Reset the file input element
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        fetchBulkJobs();
      }
    } catch (err) {
      // console.error('Bulk upload error:', err);
      const errorMessage = err.response?.data?.message || 'Failed to upload file';
      setError(errorMessage);
      toast.error(errorMessage);
      // Reset the file input on error as well
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } finally {
      setUploadLoading(false);
    }
  };

  const handleDownloadResults = async (bulkJobId) => {
    try {
      const { blob, filename } = await verificationAPI.downloadBulkResults(bulkJobId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Results downloaded successfully');
    } catch (error) {
      // console.error('Download error:', error);
      toast.error('Failed to download results');
    }
  };

  const handleCancelJob = async (bulkJobId) => {
    setJobToCancel(bulkJobId);
    setCancelDialogOpen(true);
  };

  const confirmCancelJob = async () => {
    try {
      await verificationAPI.cancelBulkJob(jobToCancel);
      toast.success('Bulk verification job cancelled');
      fetchBulkJobs(); // Refresh the list
    } catch (error) {
      // console.error('Cancel error:', error);
      toast.error(error.response?.data?.message || 'Failed to cancel job');
    } finally {
      setCancelDialogOpen(false);
      setJobToCancel(null);
    }
  };

  const closeCancelDialog = () => {
    setCancelDialogOpen(false);
    setJobToCancel(null);
  };

  const handleTabChange = (event, newValue) => {
    // Check if single verification is in progress
    if (loading) {
      setPendingTabChange(newValue);
      setTabSwitchDialogOpen(true);
      return;
    }
    
    // Check if there are any processing/pending bulk jobs
    const hasActiveJobs = bulkResults.some(job => 
      job.status === 'processing' || job.status === 'pending'
    );
    
    // Show warning if switching away from bulk tab while jobs are active
    if (hasActiveJobs && (activeTab === 1 || newValue === 1)) {
      setPendingTabChange(newValue);
      setTabSwitchDialogOpen(true);
      return;
    }
    
    // No active operations, allow tab switch
    setActiveTab(newValue);
  };

  const confirmTabSwitch = () => {
    // Cancel single verification if loading
    if (loading) {
      setLoading(false);
      setError('Verification cancelled');
    }
    
    // Switch tab
    setActiveTab(pendingTabChange);
    setTabSwitchDialogOpen(false);
    setPendingTabChange(null);
  };

  const cancelTabSwitch = () => {
    setTabSwitchDialogOpen(false);
    setPendingTabChange(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'processing':
        return 'info';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'error';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const downloadTemplate = () => {
    const selectedService = getSelectedServiceType();
    let csvContent = '';
    let filename = '';

    if (selectedService && selectedService.input_fields && selectedService.input_fields.length > 0) {
      // Create CSV header from field labels
      const headers = selectedService.input_fields.map(field => field.label).join(',');
      csvContent = `${headers}\n`;
    } else {
      // Fallback to single column with input_label
      const label = selectedService?.input_label || 'Input Value';
      csvContent = `${label}\n`;
    }

    filename = `${serviceType.replace(/\s+/g, '_')}_template.csv`;

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
  };

  if (loadingTypes) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          KYC Verification Services
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Comprehensive verification solutions for PAN, Bank Account, and TAN
        </Typography>

        <Paper sx={{ mb: 3 }}>
          <Tabs value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
            <Tab label="Single Verification" />
            <Tab label="Bulk Verification" />
          </Tabs>
        </Paper>

        {/* Single Verification Tab */}
        {activeTab === 0 && (
          <Paper sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography variant="h6" gutterBottom>
              Test Verification Service
            </Typography>

            <Box component="form" onSubmit={handleSingleVerification}>
              <Grid container spacing={2} alignItems="stretch">
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    select
                    label="Service Type"
                    value={serviceType}
                    onChange={(e) => {
                      setServiceType(e.target.value);
                      setFormData({}); // Clear form data when service type changes
                    }}
                    size="medium"
                    sx={{ height: "100%" }}
                  >
                    {serviceTypes.length === 0 ? (
                      <MenuItem value="">No services available</MenuItem>
                    ) : (
                      serviceTypes.map(type => (
                        <MenuItem key={type.id} value={type.name}>{type.name}</MenuItem>
                      ))
                    )}
                  </TextField>
                </Grid>

                {/* Dynamically render input fields based on service type */}
                {renderInputFields()}

                <Grid item xs={12} md={4}>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={loading}
                    sx={{ height: { xs: '48px', sm: '56px' } }}
                  >
                    {loading ? 'Searching...' : 'Search'}
                  </Button>
                </Grid>
              </Grid>
            </Box>

            {error && <Alert severity="error" sx={{ mt: { xs: 2, sm: 3 } }}>{error}</Alert>}

            {result && (
              <Paper sx={{ mt: { xs: 2, sm: 3 }, p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5' }}>
                <Typography variant="h6" gutterBottom>
                  Verification Result
                </Typography>
                <Alert severity={result.success ? 'success' : 'error'} sx={{ mb: 2 }}>
                  {result.success ? '✓ Verification Successful' : '✗ Verification Failed'}
                </Alert>
                {result.data && (
                  <TableContainer component={Paper}>
                    <Table>
                      <TableBody>
                        {serviceType.toLowerCase().includes('pan') && serviceType.toLowerCase().includes('cin') ? (
                          // PAN to CIN verification display
                          <>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>PAN</TableCell>
                              <TableCell>{result.data.pan || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>CIN</TableCell>
                              <TableCell sx={{ fontSize: '1.1rem', fontWeight: 700, color: '#1976d2' }}>
                                {result.data.cin || '–'}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Status</TableCell>
                              <TableCell>
                                <Box sx={{ display: 'inline-block', px: 2, py: 0.5, borderRadius: 1, bgcolor: result.data.status === 'FOUND' ? '#4caf50' : '#f44336', color: 'white', fontWeight: 600 }}>
                                  {result.data.status || '–'}
                                </Box>
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Company Name</TableCell>
                              <TableCell>{result.data.company_name || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Company Type</TableCell>
                              <TableCell>{result.data.company_type || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Company Status</TableCell>
                              <TableCell>{result.data.company_status || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Company Origin</TableCell>
                              <TableCell>{result.data.company_origin || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Account Type</TableCell>
                              <TableCell>{result.data.account_type || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>State</TableCell>
                              <TableCell>{result.data.state || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>ROC Code</TableCell>
                              <TableCell>{result.data.roc_code || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Registrar Name</TableCell>
                              <TableCell>{result.data.registrar_name || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Registration Number</TableCell>
                              <TableCell>{result.data.registration_number || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Date of Incorporation</TableCell>
                              <TableCell>{result.data.date_of_incorporation || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Company Category</TableCell>
                              <TableCell>{result.data.company_category || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Company Subcategory</TableCell>
                              <TableCell>{result.data.company_subcategory || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Message</TableCell>
                              <TableCell>{result.data.message || '–'}</TableCell>
                            </TableRow>
                          </>
                        ) : serviceType.toLowerCase().includes('pan') && serviceType.toLowerCase().includes('udyam') && serviceType.toLowerCase().includes('lite') ? (
                          // PAN to Udyam Lite - simple display
                          <>
                            <TableRow>
                              <TableCell colSpan={2} sx={{ 
                                bgcolor: '#1565c0', 
                                color: 'white', 
                                fontWeight: 700, 
                                fontSize: '1.1rem',
                                textAlign: 'center',
                                py: 1.5
                              }}>
                                Udyam Registration Number : {result.data.udyam_reg_no || 'Not Found'}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>PAN</TableCell>
                              <TableCell>{result.data.pan || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Status</TableCell>
                              <TableCell>{result.data.status || '–'}</TableCell>
                            </TableRow>
                          </>
                        ) : serviceType.toLowerCase().includes('pan') && serviceType.toLowerCase().includes('udyam') ? (
                          // PAN to Udyam Full verification display
                          <>
                            {/* Header with Udyam Registration Number */}
                            <TableRow>
                              <TableCell colSpan={2} sx={{ 
                                bgcolor: '#1565c0', 
                                color: 'white', 
                                fontWeight: 700, 
                                fontSize: '1.1rem',
                                textAlign: 'center',
                                py: 1.5
                              }}>
                                Udyam Registration Number : {result.data.udyam_reg_no || '–'}
                              </TableCell>
                            </TableRow>

                            {/* Basic Details */}
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>Name of Enterprise</TableCell>
                              <TableCell>{result.data.portal_data?.name_of_enterprise || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Organisation Type</TableCell>
                              <TableCell>{result.data.portal_data?.organisation_type || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Major Activity</TableCell>
                              <TableCell>
                                <Box sx={{ display: 'inline-block', px: 1.5, py: 0.3, bgcolor: '#4caf50', color: 'white', borderRadius: 1, fontWeight: 600 }}>
                                  {result.data.portal_data?.major_activity || '–'}
                                </Box>
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Gender</TableCell>
                              <TableCell>{result.data.portal_data?.gender || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Social Category</TableCell>
                              <TableCell>{result.data.portal_data?.social_category || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Date of Incorporation</TableCell>
                              <TableCell>{result.data.portal_data?.date_of_incorporation || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Date of Commencement of Production/Business</TableCell>
                              <TableCell>{result.data.portal_data?.date_of_commencement || '–'}</TableCell>
                            </TableRow>

                            {/* Enterprise Type Classification Table */}
                            {result.data.portal_data?.enterprise_type_history && result.data.portal_data.enterprise_type_history.length > 0 && (
                              <>
                                <TableRow>
                                  <TableCell colSpan={2} sx={{ fontWeight: 700, pt: 3, pb: 1, border: 'none' }}>
                                    Enterprise Type
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell colSpan={2} sx={{ p: 0 }}>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow sx={{ bgcolor: '#c8a415' }}>
                                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>SNo.</TableCell>
                                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>Classification Year</TableCell>
                                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>Enterprise Type</TableCell>
                                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>Classification Date</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {result.data.portal_data.enterprise_type_history.map((item, idx) => (
                                          <TableRow key={idx}>
                                            <TableCell>{item.sno}</TableCell>
                                            <TableCell>{item.classification_year}</TableCell>
                                            <TableCell>{item.enterprise_type}</TableCell>
                                            <TableCell>{item.classification_date}</TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </TableCell>
                                </TableRow>
                              </>
                            )}

                            {/* Unit/Plant Locations Table */}
                            {result.data.portal_data?.unit_locations && result.data.portal_data.unit_locations.length > 0 && (
                              <>
                                <TableRow>
                                  <TableCell colSpan={2} sx={{ fontWeight: 700, pt: 3, pb: 1, border: 'none' }}>
                                    Unit(s)/Plant Locations Details
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell colSpan={2} sx={{ p: 0 }}>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow sx={{ bgcolor: '#455a64' }}>
                                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>SN</TableCell>
                                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>Unit Name</TableCell>
                                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>Flat</TableCell>
                                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>Building</TableCell>
                                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>Village/Town</TableCell>
                                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>Block</TableCell>
                                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>Road</TableCell>
                                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>City</TableCell>
                                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>Pin</TableCell>
                                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>State</TableCell>
                                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>District</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {result.data.portal_data.unit_locations.map((unit, idx) => (
                                          <TableRow key={idx}>
                                            <TableCell>{unit.sn}</TableCell>
                                            <TableCell>{unit.unit_name}</TableCell>
                                            <TableCell>{unit.flat}</TableCell>
                                            <TableCell>{unit.building}</TableCell>
                                            <TableCell>{unit.village_town}</TableCell>
                                            <TableCell>{unit.block}</TableCell>
                                            <TableCell>{unit.road}</TableCell>
                                            <TableCell>{unit.city}</TableCell>
                                            <TableCell>{unit.pin}</TableCell>
                                            <TableCell>{unit.state}</TableCell>
                                            <TableCell>{unit.district}</TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </TableCell>
                                </TableRow>
                              </>
                            )}

                            {/* Official Address */}
                            {result.data.portal_data?.official_address && Object.keys(result.data.portal_data.official_address).length > 0 && (
                              <>
                                <TableRow>
                                  <TableCell colSpan={2} sx={{ fontWeight: 700, pt: 3, pb: 1, border: 'none' }}>
                                    Official address of Enterprise
                                  </TableCell>
                                </TableRow>
                                {Object.entries(result.data.portal_data.official_address).map(([key, value]) => (
                                  <TableRow key={key}>
                                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>
                                      {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                    </TableCell>
                                    <TableCell>{value || '–'}</TableCell>
                                  </TableRow>
                                ))}
                              </>
                            )}

                            {/* Contact */}
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Mobile</TableCell>
                              <TableCell>{result.data.portal_data?.mobile || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Email</TableCell>
                              <TableCell>{result.data.portal_data?.email || '–'}</TableCell>
                            </TableRow>

                            {/* NIC Classification Table */}
                            {result.data.portal_data?.nic_classification && result.data.portal_data.nic_classification.length > 0 && (
                              <>
                                <TableRow>
                                  <TableCell colSpan={2} sx={{ fontWeight: 700, pt: 3, pb: 1, border: 'none' }}>
                                    National Industry Classification Code(S)
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell colSpan={2} sx={{ p: 0 }}>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow sx={{ bgcolor: '#455a64' }}>
                                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>SNo.</TableCell>
                                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>Nic 2 Digit</TableCell>
                                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>Nic 4 Digit</TableCell>
                                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>Nic 5 Digit</TableCell>
                                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>Activity</TableCell>
                                          <TableCell sx={{ color: 'white', fontWeight: 600 }}>Date</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {result.data.portal_data.nic_classification.map((nic, idx) => (
                                          <TableRow key={idx}>
                                            <TableCell>{nic.sno}</TableCell>
                                            <TableCell>{nic.nic_2_digit}</TableCell>
                                            <TableCell>{nic.nic_4_digit}</TableCell>
                                            <TableCell>{nic.nic_5_digit}</TableCell>
                                            <TableCell>{nic.activity}</TableCell>
                                            <TableCell>{nic.date}</TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </TableCell>
                                </TableRow>
                              </>
                            )}

                            {/* DIC and MSME-DFO */}
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>DIC</TableCell>
                              <TableCell>{result.data.portal_data?.dic || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>MSME-DFO</TableCell>
                              <TableCell>{result.data.portal_data?.msme_dfo || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Date of Udyam Registration</TableCell>
                              <TableCell>{result.data.portal_data?.date_of_udyam_registration || '–'}</TableCell>
                            </TableRow>
                          </>
                        ) : (serviceType.toLowerCase().includes('pan 360') || serviceType.toLowerCase().includes('pan360')) ? (
                          // PAN 360 Advanced verification display
                          <>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>PAN</TableCell>
                              <TableCell>{result.data.pan || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>First Name</TableCell>
                              <TableCell>{result.data.first_name || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>Last Name</TableCell>
                              <TableCell>{result.data.last_name || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>Registered Name</TableCell>
                              <TableCell>{result.data.registered_name || result.data.name_pan_card || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>PAN Type</TableCell>
                              <TableCell>{result.data.type || result.data.pan_type || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>Gender</TableCell>
                              <TableCell>{result.data.gender || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>Date of Birth</TableCell>
                              <TableCell>{result.data.date_of_birth || result.data.dob || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>Masked Aadhar</TableCell>
                              <TableCell>{result.data.masked_aadhaar || result.data.masked_aadhar || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>Email</TableCell>
                              <TableCell>{result.data.email || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>Mobile Number</TableCell>
                              <TableCell>{result.data.mobile_number || result.data.phone || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>Aadhaar Link</TableCell>
                              <TableCell>{result.data.aadhaar_linked !== undefined ? (result.data.aadhaar_linked ? 'True' : 'False') : (result.data.aadhaar_link || '–')}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>Address</TableCell>
                              <TableCell>{typeof result.data.address === 'object' ? (result.data.address?.full_address || [result.data.address?.street, result.data.address?.city, result.data.address?.state, result.data.address?.pincode, result.data.address?.country].filter(Boolean).join(', ')) : (result.data.address || result.data.full_address || '–')}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>PAN Ref. ID</TableCell>
                              <TableCell>{result.data.ref_id || result.data.pan_ref_id || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>Status</TableCell>
                              <TableCell>{result.data.status || (result.data.valid ? 'VALID' : 'INVALID')}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>Message</TableCell>
                              <TableCell>{result.data.message || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>Name on PAN Card</TableCell>
                              <TableCell>{result.data.name_pan_card || result.data.registered_name || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>Verification ID</TableCell>
                              <TableCell sx={{ wordBreak: 'break-all' }}>{result.data.verification_id || '–'}</TableCell>
                            </TableRow>
                          </>
                        ) : (serviceType.toLowerCase().includes('employment 360') || serviceType.toLowerCase().includes('employment360')) ? (
                          // Employment 360 display
                          <>
                            <TableRow>
                              <TableCell colSpan={2} sx={{ bgcolor: '#1565c0', color: 'white', fontWeight: 700, fontSize: '1rem', textAlign: 'center', py: 1.5 }}>
                                Employee Details
                              </TableCell>
                            </TableRow>
                            {[
                              ['UAN', result.data.uan],
                              ['Employee Name', result.data.employee_name],
                              ['Gender', result.data.gender],
                              ['Date of Birth', result.data.dob],
                              ['Aadhaar Verified', result.data.aadhaar_verified ? 'Yes' : 'No'],
                              ['Email', result.data.email],
                              ['PAN', result.data.pan],
                              ['Relation', result.data.relation],
                              ['Relative Name', result.data.relative_name],
                            ].filter(([, v]) => v && v !== 'No').map(([label, value]) => (
                              <TableRow key={label}>
                                <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>{label}</TableCell>
                                <TableCell>{value || '–'}</TableCell>
                              </TableRow>
                            ))}
                            <TableRow>
                              <TableCell colSpan={2} sx={{ bgcolor: '#2e7d32', color: 'white', fontWeight: 700, fontSize: '1rem', textAlign: 'center', py: 1.5 }}>
                                Employment Details
                              </TableCell>
                            </TableRow>
                            {[
                              ['Establishment Name', result.data.establishment_name],
                              ['Member ID', result.data.member_id],
                              ['Establishment ID', result.data.establishment_id],
                              ['Joining Date', result.data.joining_date],
                              ['Exit Date', result.data.exit_date],
                              ['Leave Reason', result.data.leave_reason],
                              ['Currently Employed', result.data.employed ? 'Yes' : 'No'],
                              ['Exit Date Marked', result.data.exit_date_marked ? 'Yes' : 'No'],
                            ].filter(([, v]) => v).map(([label, value]) => (
                              <TableRow key={label}>
                                <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>{label}</TableCell>
                                <TableCell>{value || '–'}</TableCell>
                              </TableRow>
                            ))}
                            <TableRow>
                              <TableCell colSpan={2} sx={{ bgcolor: '#e65100', color: 'white', fontWeight: 700, fontSize: '1rem', textAlign: 'center', py: 1.5 }}>
                                Bank & Additional Details
                              </TableCell>
                            </TableRow>
                            {[
                              ['IFSC', result.data.ifsc],
                              ['Bank Account', result.data.bank_account],
                              ['Bank Address', result.data.bank_address],
                              ['Employer Setup Date', result.data.employer_setup_date],
                              ['Ownership Type', result.data.employer_ownership_type],
                            ].filter(([, v]) => v).map(([label, value]) => (
                              <TableRow key={label}>
                                <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>{label}</TableCell>
                                <TableCell>{value || '–'}</TableCell>
                              </TableRow>
                            ))}
                            {result.data.pf_filing_details && result.data.pf_filing_details.length > 0 && (
                              <>
                                <TableRow>
                                  <TableCell colSpan={2} sx={{ bgcolor: '#5e35b1', color: 'white', fontWeight: 700, fontSize: '1rem', textAlign: 'center', py: 1.5 }}>
                                    PF Filing Details (Recent)
                                  </TableCell>
                                </TableRow>
                                {result.data.pf_filing_details.slice(0, 6).map((pf, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>{pf.wage_month}</TableCell>
                                    <TableCell>Employees: {pf.employees_count} | Amount: Rs {Number(pf.total_amount).toLocaleString('en-IN')}</TableCell>
                                  </TableRow>
                                ))}
                              </>
                            )}
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>Verification ID</TableCell>
                              <TableCell sx={{ wordBreak: 'break-all' }}>{result.data.verification_id || '–'}</TableCell>
                            </TableRow>
                          </>
                        ) : serviceType.toLowerCase().includes('pan') && !serviceType.toLowerCase().includes('gstin') && !serviceType.toLowerCase().includes('tan') ? (
                          // Formatted PAN verification display
                          <>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>Registered Name</TableCell>
                              <TableCell>{result.data.registered_name || result.data.name_provided || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>PAN Type</TableCell>
                              <TableCell>{result.data.type || result.data.pan_type || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>PAN Ref. ID</TableCell>
                              <TableCell>{result.data.reference_id || result.data.ref_id || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>Valid</TableCell>
                              <TableCell>{result.data.valid ? 'Yes' : 'No'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>Message</TableCell>
                              <TableCell>{result.message || result.data.message || 'PAN verified successfully'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>Name Match Result</TableCell>
                              <TableCell>{result.data.name_match_result || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>Aadhaar Seeding Status</TableCell>
                              <TableCell>{result.data.aadhaar_seeding_status || 'NA'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>Last Updated At</TableCell>
                              <TableCell>{result.data.last_updated_at || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>Name on PAN Card</TableCell>
                              <TableCell>{result.data.name_on_card || result.data.registered_name || result.data.name_provided || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>PAN Status</TableCell>
                              <TableCell>{result.data.pan_status || (result.data.valid ? 'VALID' : 'INVALID')}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>Aadhaar Seeding Status Description</TableCell>
                              <TableCell>{result.data.aadhaar_seeding_status_desc || 'Aadhaar Seeding is not Applicable'}</TableCell>
                            </TableRow>
                          </>
                        ) : serviceType.toLowerCase().includes('pan') && serviceType.toLowerCase().includes('tan') ? (
                          // Formatted PAN to TAN verification display (Microvista)
                          <>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>PAN</TableCell>
                              <TableCell>{result.data.pan || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>TAN</TableCell>
                              <TableCell sx={{ 
                                fontSize: '1.3rem', 
                                fontWeight: 700, 
                                color: '#1976d2',
                                letterSpacing: '1px'
                              }}>
                                {result.data.tan || '–'}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Status</TableCell>
                              <TableCell>
                                <Box sx={{ 
                                  display: 'inline-block', 
                                  px: 2, 
                                  py: 0.5, 
                                  borderRadius: 1,
                                  bgcolor: result.data.status === 'VALID' ? '#4caf50' : '#f44336',
                                  color: 'white',
                                  fontWeight: 600
                                }}>
                                  {result.data.status || '–'}
                                </Box>
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Message</TableCell>
                              <TableCell>{result.data.message || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Reference ID</TableCell>
                              <TableCell>{result.data.reference_id || '–'}</TableCell>
                            </TableRow>
                          </>
                        ) : serviceType.toLowerCase().includes('tan') && !serviceType.toLowerCase().includes('pan') ? (
                          // Formatted TAN verification display (Microvista)
                          <>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>TAN</TableCell>
                              <TableCell sx={{ 
                                fontSize: '1.1rem', 
                                fontWeight: 700, 
                                color: '#1976d2',
                                letterSpacing: '0.5px'
                              }}>
                                {result.data.tan || '–'}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Status</TableCell>
                              <TableCell>
                                <Box sx={{ 
                                  display: 'inline-block', 
                                  px: 2, 
                                  py: 0.5, 
                                  borderRadius: 1,
                                  bgcolor: result.data.status === 'VALID' ? '#4caf50' : '#f44336',
                                  color: 'white',
                                  fontWeight: 600
                                }}>
                                  {result.data.status || '–'}
                                </Box>
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Name</TableCell>
                              <TableCell>{result.data.name || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>First Name</TableCell>
                              <TableCell>{result.data.first_name || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Middle Name</TableCell>
                              <TableCell>{result.data.middle_name || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Last Name</TableCell>
                              <TableCell>{result.data.last_name || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell colSpan={2} sx={{ fontWeight: 700, bgcolor: '#e0e0e0', py: 1.5 }}>
                                Address Details
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>Address Line 1</TableCell>
                              <TableCell>{result.data.address_line1 || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>Address Line 2</TableCell>
                              <TableCell>{result.data.address_line2 || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>Address Line 3</TableCell>
                              <TableCell>{result.data.address_line3 || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>Address Line 4</TableCell>
                              <TableCell>{result.data.address_line4 || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>Address Line 5</TableCell>
                              <TableCell>{result.data.address_line5 || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>State</TableCell>
                              <TableCell>{result.data.state || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>Pincode</TableCell>
                              <TableCell>{result.data.pincode || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell colSpan={2} sx={{ fontWeight: 700, bgcolor: '#e0e0e0', py: 1.5 }}>
                                Contact Details
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>Phone Number</TableCell>
                              <TableCell>{result.data.phone_number || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>Email 1</TableCell>
                              <TableCell>{result.data.email_1 || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>Email 2</TableCell>
                              <TableCell>{result.data.email_2 || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Date of Allotment</TableCell>
                              <TableCell>{result.data.date_of_allotment || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Message</TableCell>
                              <TableCell>{result.data.message || '–'}</TableCell>
                            </TableRow>
                          </>
                        ) : serviceType.toLowerCase().includes('bank') ? (
                          // Formatted Bank Account verification display
                          <>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>Account Number</TableCell>
                              <TableCell>{result.data.account_number || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Name at Bank</TableCell>
                              <TableCell>{result.data.name_at_bank || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Bank Name</TableCell>
                              <TableCell>{result.data.bank_name || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>IFSC Code</TableCell>
                              <TableCell>{result.data.ifsc || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Branch</TableCell>
                              <TableCell>{result.data.branch || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>City</TableCell>
                              <TableCell>{result.data.city || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>MICR Code</TableCell>
                              <TableCell>{result.data.micr || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Account Status</TableCell>
                              <TableCell>
                                <Box sx={{ 
                                  display: 'inline-block', 
                                  px: 2, 
                                  py: 0.5, 
                                  borderRadius: 1,
                                  bgcolor: result.data.account_status === 'VALID' ? '#4caf50' : '#f44336',
                                  color: 'white',
                                  fontWeight: 600
                                }}>
                                  {result.data.account_status || '–'}
                                </Box>
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Account Status Code</TableCell>
                              <TableCell>{result.data.account_status_code || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Name Match Result</TableCell>
                              <TableCell>{result.data.name_match_result || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Name Match Score</TableCell>
                              <TableCell>{result.data.name_match_score || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>UTR</TableCell>
                              <TableCell>{result.data.utr || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Reference ID</TableCell>
                              <TableCell>{result.data.reference_id || '–'}</TableCell>
                            </TableRow>
                            {result.data.ifsc_details && typeof result.data.ifsc_details === 'object' && (
                              <>
                                <TableRow>
                                  <TableCell colSpan={2} sx={{ fontWeight: 700, bgcolor: '#e0e0e0', py: 1.5 }}>
                                    IFSC Details
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>Bank</TableCell>
                                  <TableCell>{result.data.ifsc_details.bank || '–'}</TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>Branch</TableCell>
                                  <TableCell>{result.data.ifsc_details.branch || '–'}</TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>Address</TableCell>
                                  <TableCell>{result.data.ifsc_details.address || '–'}</TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>City</TableCell>
                                  <TableCell>{result.data.ifsc_details.city || '–'}</TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>State</TableCell>
                                  <TableCell>{result.data.ifsc_details.state || '–'}</TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>IFSC Subcode</TableCell>
                                  <TableCell>{result.data.ifsc_details.ifsc_subcode || '–'}</TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>SWIFT Code</TableCell>
                                  <TableCell>{result.data.ifsc_details.swift_code || '–'}</TableCell>
                                </TableRow>
                              </>
                            )}
                          </>
                        ) : serviceType.toLowerCase().includes('cin') ? (
                          // Formatted CIN verification display based on actual Cashfree response
                          <>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>CIN</TableCell>
                              <TableCell>{result.data.cin || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Company Name</TableCell>
                              <TableCell>{result.data.company_name || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Registration Number</TableCell>
                              <TableCell>{result.data.registration_number || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Incorporation Date</TableCell>
                              <TableCell>{result.data.incorporation_date || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>CIN Status</TableCell>
                              <TableCell>
                                <Box sx={{ 
                                  display: 'inline-block', 
                                  px: 2, 
                                  py: 0.5, 
                                  borderRadius: 1,
                                  bgcolor: result.data.cin_status === 'ACTIVE' ? '#4caf50' : '#f44336',
                                  color: 'white',
                                  fontWeight: 600
                                }}>
                                  {result.data.cin_status || '–'}
                                </Box>
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Email</TableCell>
                              <TableCell>{result.data.email || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Incorporation Country</TableCell>
                              <TableCell>{result.data.incorporation_country || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Reference ID</TableCell>
                              <TableCell>{result.data.reference_id || '–'}</TableCell>
                            </TableRow>
                            {result.data.director_details && (
                              <TableRow>
                                <TableCell colSpan={2} sx={{ fontWeight: 700, bgcolor: '#e0e0e0', py: 1.5 }}>
                                  Director Details
                                </TableCell>
                              </TableRow>
                            )}
                            {result.data.director_details && Array.isArray(result.data.director_details) && result.data.director_details.map((director, index) => (
                              <React.Fragment key={index}>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>Director {index + 1} - Name</TableCell>
                                  <TableCell>{director.name || '–'}</TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>Director {index + 1} - Date of Birth</TableCell>
                                  <TableCell>{director.dob || '–'}</TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>Director {index + 1} - Designation</TableCell>
                                  <TableCell>{director.designation || '–'}</TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>Director {index + 1} - DIN</TableCell>
                                  <TableCell>{director.din || '–'}</TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>Director {index + 1} - Address</TableCell>
                                  <TableCell>{director.address || '–'}</TableCell>
                                </TableRow>
                              </React.Fragment>
                            ))}
                            {result.data.director_details && !Array.isArray(result.data.director_details) && (
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>Director Details</TableCell>
                                <TableCell>{typeof result.data.director_details === 'string' ? result.data.director_details : JSON.stringify(result.data.director_details)}</TableCell>
                              </TableRow>
                            )}
                          </>
                        ) : serviceType.toLowerCase().includes('driving') || serviceType.toLowerCase().includes('license') ? (
                          // Formatted Driving License verification display based on actual Cashfree response
                          <>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>DL Number</TableCell>
                              <TableCell>{result.data.dl_number || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Date of Birth</TableCell>
                              <TableCell>{result.data.dob || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Status</TableCell>
                              <TableCell>
                                <Box sx={{ 
                                  display: 'inline-block', 
                                  px: 2, 
                                  py: 0.5, 
                                  borderRadius: 1,
                                  bgcolor: result.data.status === 'VALID' ? '#4caf50' : '#f44336',
                                  color: 'white',
                                  fontWeight: 600
                                }}>
                                  {result.data.status || '–'}
                                </Box>
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Full Name</TableCell>
                              <TableCell>{result.data.name || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Father's / Husband's Name</TableCell>
                              <TableCell>{result.data.father_or_husband_name || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Date of Issue</TableCell>
                              <TableCell>{result.data.date_of_issue || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>DL Status</TableCell>
                              <TableCell>{result.data.dl_status || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Class of Vehicle</TableCell>
                              <TableCell>{Array.isArray(result.data.class_of_vehicle) ? result.data.class_of_vehicle.join(', ') : (result.data.class_of_vehicle || '–')}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Permanent Address</TableCell>
                              <TableCell>{result.data.permanent_address || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Temporary Address</TableCell>
                              <TableCell>{result.data.temporary_address || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell colSpan={2} sx={{ fontWeight: 700, bgcolor: '#e0e0e0', py: 1.5 }}>
                                Driving License Validity
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>Non Transport (From)</TableCell>
                              <TableCell>{result.data.non_transport_from || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>Non Transport (To)</TableCell>
                              <TableCell>{result.data.non_transport_to || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>Transport (From)</TableCell>
                              <TableCell>{result.data.transport_from || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>Transport (To)</TableCell>
                              <TableCell>{result.data.transport_to || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Reference ID</TableCell>
                              <TableCell>{result.data.reference_id || '–'}</TableCell>
                            </TableRow>
                            {result.data.photo && (
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Photo</TableCell>
                                <TableCell>
                                  <img src={result.data.photo} alt="DL Photo" style={{ maxWidth: '200px', borderRadius: '8px' }} />
                                </TableCell>
                              </TableRow>
                            )}
                          </>
                        ) : serviceType.toLowerCase().includes('pan to gstin') || serviceType.toLowerCase().includes('pan') && serviceType.toLowerCase().includes('gstin') ? (
                          // Formatted PAN to GSTIN search display
                          <>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>PAN</TableCell>
                              <TableCell>{result.data.data?.pan || result.data.pan || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Total GSTINs Found</TableCell>
                              <TableCell>
                                <Box sx={{ 
                                  display: 'inline-block', 
                                  px: 2, 
                                  py: 0.5, 
                                  borderRadius: 1,
                                  bgcolor: '#2196f3',
                                  color: 'white',
                                  fontWeight: 600
                                }}>
                                  {result.data.data?.count || result.data.count || 0}
                                </Box>
                              </TableCell>
                            </TableRow>
                            {(result.data.data?.gstins || result.data.gstins || []).length > 0 && (
                              <>
                                <TableRow>
                                  <TableCell colSpan={2} sx={{ fontWeight: 700, bgcolor: '#e0e0e0', py: 1.5 }}>
                                    GSTIN Details
                                  </TableCell>
                                </TableRow>
                                {(result.data.data?.gstins || result.data.gstins || []).map((gstinData, index) => (
                                  <React.Fragment key={index}>
                                    <TableRow>
                                      <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>GSTIN {index + 1}</TableCell>
                                      <TableCell>{gstinData.gstin || '–'}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                      <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>Status</TableCell>
                                      <TableCell>
                                        <Box sx={{ 
                                          display: 'inline-block', 
                                          px: 2, 
                                          py: 0.5, 
                                          borderRadius: 1,
                                          bgcolor: gstinData.status === 'Active' ? '#4caf50' : '#f44336',
                                          color: 'white',
                                          fontWeight: 600
                                        }}>
                                          {gstinData.status || '–'}
                                        </Box>
                                      </TableCell>
                                    </TableRow>
                                    <TableRow>
                                      <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>State</TableCell>
                                      <TableCell>{gstinData.state || '–'}</TableCell>
                                    </TableRow>
                                  </React.Fragment>
                                ))}
                              </>
                            )}
                          </>
                        ) : serviceType.toLowerCase().includes('pan') && serviceType.toLowerCase().includes('udyam') && serviceType.toLowerCase().includes('lite') ? (
                          // PAN to Udyam Lite - just show Udyam number
                          <>
                            <TableRow>
                              <TableCell colSpan={2} sx={{ 
                                bgcolor: '#1565c0', 
                                color: 'white', 
                                fontWeight: 700, 
                                fontSize: '1.1rem',
                                textAlign: 'center',
                                py: 1.5
                              }}>
                                Udyam Registration Number : {result.data.udyam_reg_no || 'Not Found'}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>PAN</TableCell>
                              <TableCell>{result.data.pan || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Status</TableCell>
                              <TableCell>{result.data.status || '–'}</TableCell>
                            </TableRow>
                          </>
                        ) : serviceType.toLowerCase().includes('msme') || serviceType.toLowerCase().includes('udyam') ? (
                          // Formatted MSME verification display
                          <>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>Udyam Registration Number</TableCell>
                              <TableCell>{result.data.udyam_registration_number || result.data.urn || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Name of Enterprise</TableCell>
                              <TableCell>{result.data.name_of_enterprise || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Organisation Type</TableCell>
                              <TableCell>{result.data.organisation_type || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Major Activity</TableCell>
                              <TableCell>{result.data.major_activity || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Gender</TableCell>
                              <TableCell>{result.data.gender || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Social Category</TableCell>
                              <TableCell>{result.data.social_category || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Date of Incorporation</TableCell>
                              <TableCell>{result.data.date_of_incorporation || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Date of Commencement of Production/Business</TableCell>
                              <TableCell>{result.data.date_of_commencement || '–'}</TableCell>
                            </TableRow>
                            {result.data.enterprise_type_history && result.data.enterprise_type_history.length > 0 && (
                              <>
                                <TableRow>
                                  <TableCell colSpan={2} sx={{ fontWeight: 700, bgcolor: '#e0e0e0', py: 1.5 }}>Enterprise Type</TableCell>
                                </TableRow>
                                {result.data.enterprise_type_history.map((et, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>{et.classification_year}</TableCell>
                                    <TableCell>{et.enterprise_type} (Classified: {et.classification_date})</TableCell>
                                  </TableRow>
                                ))}
                              </>
                            )}
                            {result.data.unit_locations && result.data.unit_locations.length > 0 && (
                              <>
                                <TableRow>
                                  <TableCell colSpan={2} sx={{ fontWeight: 700, bgcolor: '#e0e0e0', py: 1.5 }}>Unit(s)/Plant Locations</TableCell>
                                </TableRow>
                                {result.data.unit_locations.map((unit, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>{unit.unit_name || `Unit ${idx + 1}`}</TableCell>
                                    <TableCell>{[unit.flat, unit.building, unit.village_town, unit.block, unit.road, unit.city, unit.state, unit.pin].filter(Boolean).join(', ') || '–'}</TableCell>
                                  </TableRow>
                                ))}
                              </>
                            )}
                            {result.data.official_address && typeof result.data.official_address === 'object' && (
                              <>
                                <TableRow>
                                  <TableCell colSpan={2} sx={{ fontWeight: 700, bgcolor: '#e0e0e0', py: 1.5 }}>Official Address of Enterprise</TableCell>
                                </TableRow>
                                {Object.entries(result.data.official_address).filter(([,v]) => v).map(([key, val]) => (
                                  <TableRow key={key}>
                                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</TableCell>
                                    <TableCell>{val}</TableCell>
                                  </TableRow>
                                ))}
                              </>
                            )}
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Mobile</TableCell>
                              <TableCell>{result.data.mobile || result.data.mobile_no || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Email</TableCell>
                              <TableCell>{result.data.email || '–'}</TableCell>
                            </TableRow>
                            {result.data.nic_classification && result.data.nic_classification.length > 0 && (
                              <>
                                <TableRow>
                                  <TableCell colSpan={2} sx={{ fontWeight: 700, bgcolor: '#e0e0e0', py: 1.5 }}>National Industry Classification Code(S)</TableCell>
                                </TableRow>
                                {result.data.nic_classification.map((nic, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>NIC {idx + 1}</TableCell>
                                    <TableCell>{[nic.nic_2_digit, nic.nic_4_digit, nic.nic_5_digit, nic.activity, nic.date].filter(Boolean).join(' | ')}</TableCell>
                                  </TableRow>
                                ))}
                              </>
                            )}
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>DIC</TableCell>
                              <TableCell>{result.data.dic || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>MSME-DFO</TableCell>
                              <TableCell>{result.data.msme_dfo || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Date of Udyam Registration</TableCell>
                              <TableCell>{result.data.date_of_udyam_registration || '–'}</TableCell>
                            </TableRow>
                          </>
                        ) : serviceType.toLowerCase().includes('gst lite') || serviceType.toLowerCase().includes('gst_lite') ? (
                          // GST Lite display
                          <>
                            {[
                              ['GSTIN', result.data.gstin],
                              ['Legal Name of Business', result.data.legal_name],
                              ['Trade Name of Business', result.data.trade_name],
                              ['Date of Registration', result.data.registration_date],
                              ['Constitution of Business', result.data.constitution],
                              ['GSTIN Status', result.data.status],
                              ['Tax Payer Type', result.data.taxpayer_type],
                              ['Principal Place Address', result.data.principal_place_address],
                            ].map(([label, value]) => (
                              <TableRow key={label}>
                                <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>{label}</TableCell>
                                <TableCell>{value || '–'}</TableCell>
                              </TableRow>
                            ))}
                          </>
                        ) : serviceType.toLowerCase().includes('gstin') || serviceType.toLowerCase().includes('gst') ? (
                          // Comprehensive GSTIN verification display
                          <>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>GSTIN</TableCell>
                              <TableCell>{result.data.gstin || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Legal Name of Business</TableCell>
                              <TableCell>{result.data.legal_name || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Trade Name of Business</TableCell>
                              <TableCell>{result.data.trade_name || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Date of Registration</TableCell>
                              <TableCell>{result.data.registration_date || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Constitution of Business</TableCell>
                              <TableCell>{result.data.constitution || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>GSTIN Status</TableCell>
                              <TableCell>
                                <Box sx={{ 
                                  display: 'inline-block', 
                                  px: 2, 
                                  py: 0.5, 
                                  borderRadius: 1,
                                  bgcolor: result.data.status === 'Active' ? '#4caf50' : '#f44336',
                                  color: 'white',
                                  fontWeight: 600
                                }}>
                                  {result.data.status || '–'}
                                </Box>
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Tax Payer Type</TableCell>
                              <TableCell>{result.data.taxpayer_type || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Principal Place Address</TableCell>
                              <TableCell>{result.data.principal_place_address || '–'}</TableCell>
                            </TableRow>
                            
                            {/* Administrative Office */}
                            {(result.data.admin_state || result.data.admin_zone || result.data.admin_commissionerate || result.data.admin_division || result.data.admin_range) && (
                              <>
                                <TableRow>
                                  <TableCell colSpan={2} sx={{ bgcolor: '#e3f2fd', fontWeight: 700, fontSize: '1.1em', py: 1.5 }}>
                                    Administrative Office
                                  </TableCell>
                                </TableRow>
                                {result.data.admin_state && (
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>State</TableCell>
                                    <TableCell>{result.data.admin_state}</TableCell>
                                  </TableRow>
                                )}
                                {result.data.admin_zone && (
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>Zone</TableCell>
                                    <TableCell>{result.data.admin_zone}</TableCell>
                                  </TableRow>
                                )}
                                {result.data.admin_commissionerate && (
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>Commissionerate</TableCell>
                                    <TableCell>{result.data.admin_commissionerate}</TableCell>
                                  </TableRow>
                                )}
                                {result.data.admin_division && (
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>Division</TableCell>
                                    <TableCell>{result.data.admin_division}</TableCell>
                                  </TableRow>
                                )}
                                {result.data.admin_range && (
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>Range</TableCell>
                                    <TableCell>{result.data.admin_range}</TableCell>
                                  </TableRow>
                                )}
                              </>
                            )}
                            
                            {/* Other Office */}
                            {(result.data.other_state || result.data.other_zone || result.data.other_division || result.data.other_charge) && (
                              <>
                                <TableRow>
                                  <TableCell colSpan={2} sx={{ bgcolor: '#e3f2fd', fontWeight: 700, fontSize: '1.1em', py: 1.5 }}>
                                    Other Office
                                  </TableCell>
                                </TableRow>
                                {result.data.other_state && (
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>State</TableCell>
                                    <TableCell>{result.data.other_state}</TableCell>
                                  </TableRow>
                                )}
                                {result.data.other_zone && (
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>Zone</TableCell>
                                    <TableCell>{result.data.other_zone}</TableCell>
                                  </TableRow>
                                )}
                                {result.data.other_division && (
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>Division</TableCell>
                                    <TableCell>{result.data.other_division}</TableCell>
                                  </TableRow>
                                )}
                                {result.data.other_charge && (
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>Charge</TableCell>
                                    <TableCell>{result.data.other_charge}</TableCell>
                                  </TableRow>
                                )}
                              </>
                            )}
                            
                            {/* Aadhaar and eKYC */}
                            {result.data.aadhaar_authenticated && (
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Whether Aadhaar Authenticated?</TableCell>
                                <TableCell>{result.data.aadhaar_authenticated} {result.data.aadhaar_auth_date && `(On ${result.data.aadhaar_auth_date})`}</TableCell>
                              </TableRow>
                            )}
                            {result.data.ekyc_verified && (
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Whether e-KYC Verified?</TableCell>
                                <TableCell>{result.data.ekyc_verified}</TableCell>
                              </TableRow>
                            )}
                            
                            {/* Business Activity */}
                            {result.data.core_business_activity && (
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Nature Of Core Business Activity</TableCell>
                                <TableCell>{result.data.core_business_activity}</TableCell>
                              </TableRow>
                            )}
                            {result.data.business_activities && (
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Nature of Business Activities</TableCell>
                                <TableCell>{result.data.business_activities}</TableCell>
                              </TableRow>
                            )}
                            
                            {/* HSN Codes */}
                            {((result.data.hsn_goods && result.data.hsn_goods.length > 0) || (result.data.hsn_services && result.data.hsn_services.length > 0)) && (
                              <TableRow>
                                <TableCell colSpan={2} sx={{ p: 0 }}>
                                  <Box sx={{ p: 2, bgcolor: '#fafafa' }}>
                                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>Dealing In Goods and Services</Typography>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                      <thead>
                                        <tr style={{ backgroundColor: '#e3f2fd' }}>
                                          <th colSpan="2" style={{ padding: '12px', border: '1px solid #ddd', fontWeight: 700 }}>Goods</th>
                                          <th colSpan="2" style={{ padding: '12px', border: '1px solid #ddd', fontWeight: 700 }}>Services</th>
                                        </tr>
                                        <tr style={{ backgroundColor: '#f5f5f5' }}>
                                          <th style={{ padding: '10px', border: '1px solid #ddd', width: '15%' }}>HSN</th>
                                          <th style={{ padding: '10px', border: '1px solid #ddd', width: '35%' }}>Description</th>
                                          <th style={{ padding: '10px', border: '1px solid #ddd', width: '15%' }}>HSN</th>
                                          <th style={{ padding: '10px', border: '1px solid #ddd', width: '35%' }}>Description</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {Array.from({length: Math.max(result.data.hsn_goods?.length || 0, result.data.hsn_services?.length || 0)}).map((_, index) => (
                                          <tr key={index}>
                                            <td style={{ padding: '10px', border: '1px solid #ddd' }}>{result.data.hsn_goods?.[index]?.hsn || ''}</td>
                                            <td style={{ padding: '10px', border: '1px solid #ddd' }}>{result.data.hsn_goods?.[index]?.description || ''}</td>
                                            <td style={{ padding: '10px', border: '1px solid #ddd' }}>{result.data.hsn_services?.[index]?.hsn || ''}</td>
                                            <td style={{ padding: '10px', border: '1px solid #ddd' }}>{result.data.hsn_services?.[index]?.description || ''}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                    <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: '#666' }}>
                                      HSN: Harmonized System of Nomenclature of Goods and Services
                                    </Typography>
                                    
                                    {/* Buttons below HSN table */}
                                    <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                                      {result.data.filing_details && (result.data.filing_details.gstr3b?.length > 0 || result.data.filing_details.gstr1?.length > 0 || result.data.filing_details.gstr9?.length > 0 || result.data.filing_details.gstr9c?.length > 0) && (
                                        <Button 
                                          variant="contained"
                                          onClick={() => {
                                            setShowFilingTable(!showFilingTable);
                                            if (!showFilingTable) setShowFrequencyTable(false); // Hide frequency table when showing filing table
                                          }}
                                          size="small"
                                        >
                                          Show Filing Table
                                        </Button>
                                      )}
                                      
                                      {result.data.filing_frequency && result.data.filing_frequency.length > 0 && (
                                        <Button 
                                          variant="contained"
                                          onClick={() => {
                                            setShowFrequencyTable(!showFrequencyTable);
                                            if (!showFrequencyTable) setShowFilingTable(false); // Hide filing table when showing frequency table
                                          }}
                                          size="small"
                                        >
                                          Show Return Filing Frequency
                                        </Button>
                                      )}
                                    </Box>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            )}
                            
                            {/* Filing Details - shown when button clicked */}
                            {showFilingTable && result.data.filing_details && (result.data.filing_details.gstr3b?.length > 0 || result.data.filing_details.gstr1?.length > 0 || result.data.filing_details.gstr9?.length > 0 || result.data.filing_details.gstr9c?.length > 0) && (
                              <TableRow>
                                <TableCell colSpan={2} sx={{ p: 0 }}>
                                  <Box sx={{ p: 2, bgcolor: '#fafafa' }}>
                                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>Filing Details</Typography>
                                    
                                    {/* Financial Year Dropdown and Search */}
                                    <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
                                      <TextField
                                        select
                                        size="small"
                                        value={selectedFY}
                                        onChange={(e) => setSelectedFY(e.target.value)}
                                        label="Select Financial Year"
                                        sx={{ minWidth: 200 }}
                                      >
                                        <MenuItem value="">Select Year</MenuItem>
                                        {getFinancialYears().map(fy => (
                                          <MenuItem key={fy} value={fy}>{fy}</MenuItem>
                                        ))}
                                      </TextField>
                                      <Button 
                                        variant="contained"
                                        onClick={handleFilingSearch}
                                        disabled={!selectedFY}
                                        size="small"
                                      >
                                        Search
                                      </Button>
                                    </Box>
                                    
                                    {/* Display filtered filing data */}
                                    {filingYearData && (
                                      <>
                                        {filingYearData.gstr3b?.length > 0 && (
                                          <Box sx={{ mb: 3 }}>
                                            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Filing details for GSTR3B</Typography>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
                                              <thead>
                                                <tr style={{ backgroundColor: '#e3f2fd' }}>
                                                  <th style={{ padding: '10px', border: '1px solid #ddd' }}>Financial Year</th>
                                                  <th style={{ padding: '10px', border: '1px solid #ddd' }}>Tax Period</th>
                                                  <th style={{ padding: '10px', border: '1px solid #ddd' }}>Date of filing</th>
                                                  <th style={{ padding: '10px', border: '1px solid #ddd' }}>Status</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {filingYearData.gstr3b.map((filing, index) => (
                                                  <tr key={index}>
                                                    <td style={{ padding: '10px', border: '1px solid #ddd' }}>{filing.financialYear}</td>
                                                    <td style={{ padding: '10px', border: '1px solid #ddd' }}>{filing.taxPeriod}</td>
                                                    <td style={{ padding: '10px', border: '1px solid #ddd' }}>{filing.dateOfFiling}</td>
                                                    <td style={{ padding: '10px', border: '1px solid #ddd' }}>{filing.status}</td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          </Box>
                                        )}
                                        {filingYearData.gstr1?.length > 0 && (
                                          <Box sx={{ mb: 3 }}>
                                            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Filing details for GSTR-1/IFF</Typography>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
                                              <thead>
                                                <tr style={{ backgroundColor: '#e3f2fd' }}>
                                                  <th style={{ padding: '10px', border: '1px solid #ddd' }}>Financial Year</th>
                                                  <th style={{ padding: '10px', border: '1px solid #ddd' }}>Tax Period</th>
                                                  <th style={{ padding: '10px', border: '1px solid #ddd' }}>Date of filing</th>
                                                  <th style={{ padding: '10px', border: '1px solid #ddd' }}>Status</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {filingYearData.gstr1.map((filing, index) => (
                                                  <tr key={index}>
                                                    <td style={{ padding: '10px', border: '1px solid #ddd' }}>{filing.financialYear}</td>
                                                    <td style={{ padding: '10px', border: '1px solid #ddd' }}>{filing.taxPeriod}</td>
                                                    <td style={{ padding: '10px', border: '1px solid #ddd' }}>{filing.dateOfFiling}</td>
                                                    <td style={{ padding: '10px', border: '1px solid #ddd' }}>{filing.status}</td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          </Box>
                                        )}
                                        {filingYearData.gstr9?.length > 0 && (
                                          <Box sx={{ mb: 3 }}>
                                            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Filing details for GSTR9</Typography>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
                                              <thead>
                                                <tr style={{ backgroundColor: '#e3f2fd' }}>
                                                  <th style={{ padding: '10px', border: '1px solid #ddd' }}>Financial Year</th>
                                                  <th style={{ padding: '10px', border: '1px solid #ddd' }}>Tax Period</th>
                                                  <th style={{ padding: '10px', border: '1px solid #ddd' }}>Date of filing</th>
                                                  <th style={{ padding: '10px', border: '1px solid #ddd' }}>Status</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {filingYearData.gstr9.map((filing, index) => (
                                                  <tr key={index}>
                                                    <td style={{ padding: '10px', border: '1px solid #ddd' }}>{filing.financialYear}</td>
                                                    <td style={{ padding: '10px', border: '1px solid #ddd' }}>{filing.taxPeriod}</td>
                                                    <td style={{ padding: '10px', border: '1px solid #ddd' }}>{filing.dateOfFiling}</td>
                                                    <td style={{ padding: '10px', border: '1px solid #ddd' }}>{filing.status}</td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          </Box>
                                        )}
                                        {filingYearData.gstr9c?.length > 0 && (
                                          <Box>
                                            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Filing details for GSTR9C</Typography>
                                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                              <thead>
                                                <tr style={{ backgroundColor: '#e3f2fd' }}>
                                                  <th style={{ padding: '10px', border: '1px solid #ddd' }}>Financial Year</th>
                                                  <th style={{ padding: '10px', border: '1px solid #ddd' }}>Tax Period</th>
                                                  <th style={{ padding: '10px', border: '1px solid #ddd' }}>Date of filing</th>
                                                  <th style={{ padding: '10px', border: '1px solid #ddd' }}>Status</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {filingYearData.gstr9c.map((filing, index) => (
                                                  <tr key={index}>
                                                    <td style={{ padding: '10px', border: '1px solid #ddd' }}>{filing.financialYear}</td>
                                                    <td style={{ padding: '10px', border: '1px solid #ddd' }}>{filing.taxPeriod}</td>
                                                    <td style={{ padding: '10px', border: '1px solid #ddd' }}>{filing.dateOfFiling}</td>
                                                    <td style={{ padding: '10px', border: '1px solid #ddd' }}>{filing.status}</td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          </Box>
                                        )}
                                        {filingYearData.gstr3b?.length === 0 && filingYearData.gstr1?.length === 0 && filingYearData.gstr9?.length === 0 && filingYearData.gstr9c?.length === 0 && (
                                          <Alert severity="info">No filing data available for the selected financial year.</Alert>
                                        )}
                                      </>
                                    )}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            )}
                            
                            {/* Filing Frequency - shown when button clicked */}
                            {showFrequencyTable && result.data.filing_frequency && result.data.filing_frequency.length > 0 && (
                              <TableRow>
                                <TableCell colSpan={2} sx={{ p: 0 }}>
                                  <Box sx={{ p: 2, bgcolor: '#fafafa' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                      <Typography variant="h6" sx={{ fontWeight: 700 }}>Return Filing Frequency</Typography>
                                      {result.data.filing_frequency.length > 1 && (
                                        <Button 
                                          variant="outlined"
                                          size="small"
                                          onClick={() => setFrequencyPeriod(frequencyPeriod === 'current' ? 'previous' : 'current')}
                                        >
                                          {frequencyPeriod === 'current' ? 'Filing Frequency of Previous FY' : 'Filing Frequency of Current Year'}
                                        </Button>
                                      )}
                                    </Box>
                                    
                                    {(() => {
                                      const frequencyData = getFrequencyData();
                                      
                                      if (!frequencyData) {
                                        return (
                                          <Alert severity="warning">
                                            Taxpayer type is not active for the selected period.
                                          </Alert>
                                        );
                                      }
                                      
                                      return (
                                        <>
                                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                            <thead>
                                              <tr style={{ backgroundColor: '#1976d2', color: 'white' }}>
                                                <th style={{ padding: '10px', border: '1px solid #ddd' }}>Financial Year</th>
                                                <th style={{ padding: '10px', border: '1px solid #ddd' }}>Quarter</th>
                                                <th style={{ padding: '10px', border: '1px solid #ddd' }}>Frequency</th>
                                                <th style={{ padding: '10px', border: '1px solid #ddd' }}>Quarter</th>
                                                <th style={{ padding: '10px', border: '1px solid #ddd' }}>Frequency</th>
                                                <th style={{ padding: '10px', border: '1px solid #ddd' }}>Quarter</th>
                                                <th style={{ padding: '10px', border: '1px solid #ddd' }}>Frequency</th>
                                                <th style={{ padding: '10px', border: '1px solid #ddd' }}>Quarter</th>
                                                <th style={{ padding: '10px', border: '1px solid #ddd' }}>Frequency</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              <tr>
                                                <td style={{ padding: '10px', border: '1px solid #ddd' }}>{frequencyData.financialYear}</td>
                                                <td style={{ padding: '10px', border: '1px solid #ddd' }}>{frequencyData.q1Quarter}</td>
                                                <td style={{ padding: '10px', border: '1px solid #ddd' }}>{frequencyData.q1Frequency}</td>
                                                <td style={{ padding: '10px', border: '1px solid #ddd' }}>{frequencyData.q2Quarter}</td>
                                                <td style={{ padding: '10px', border: '1px solid #ddd' }}>{frequencyData.q2Frequency}</td>
                                                <td style={{ padding: '10px', border: '1px solid #ddd' }}>{frequencyData.q3Quarter}</td>
                                                <td style={{ padding: '10px', border: '1px solid #ddd' }}>{frequencyData.q3Frequency}</td>
                                                <td style={{ padding: '10px', border: '1px solid #ddd' }}>{frequencyData.q4Quarter}</td>
                                                <td style={{ padding: '10px', border: '1px solid #ddd' }}>{frequencyData.q4Frequency}</td>
                                              </tr>
                                            </tbody>
                                          </table>
                                          <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic', color: 'text.secondary' }}>
                                            * Not Applicable implies either the taxpayer type is not registered or Cancelled or Composition.
                                          </Typography>
                                        </>
                                      );
                                    })()}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            )}
                          </>
                        ) : serviceType.toLowerCase().includes('voter') ? (
                          // Formatted Voter ID verification display
                          <>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>Epic Number</TableCell>
                              <TableCell>
                                <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 700 }}>
                                  {result.data.epic_number || '–'}
                                </Typography>
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Status</TableCell>
                              <TableCell>
                                <Box sx={{ 
                                  display: 'inline-block', 
                                  px: 2, 
                                  py: 0.5, 
                                  borderRadius: 1,
                                  bgcolor: result.data.status === 'VALID' ? '#4caf50' : '#f44336',
                                  color: 'white',
                                  fontWeight: 600
                                }}>
                                  {result.data.status || '–'}
                                </Box>
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Name</TableCell>
                              <TableCell>{result.data.name || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Name In Regional Lang</TableCell>
                              <TableCell>{result.data.name_in_regional_lang || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Age</TableCell>
                              <TableCell>{result.data.age || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Relation Type</TableCell>
                              <TableCell>{result.data.relation_type || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Relation Name</TableCell>
                              <TableCell>{result.data.relation_name || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Relation Name In Regional Lang</TableCell>
                              <TableCell>{result.data.relation_name_in_regional_lang || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Father Name</TableCell>
                              <TableCell>{result.data.father_name || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Dob</TableCell>
                              <TableCell>{result.data.dob || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Gender</TableCell>
                              <TableCell>{result.data.gender || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>State</TableCell>
                              <TableCell>{result.data.state || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Assembly Constituency Number</TableCell>
                              <TableCell>{result.data.assembly_constituency_number || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Assembly Constituency</TableCell>
                              <TableCell>{result.data.assembly_constituency || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Parliamentary Constituency Number</TableCell>
                              <TableCell>{result.data.parliamentary_constituency_number || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Parliamentary Constituency</TableCell>
                              <TableCell>{result.data.parliamentary_constituency || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Part Number</TableCell>
                              <TableCell>{result.data.part_number || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Part Name</TableCell>
                              <TableCell>{result.data.part_name || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Serial Number</TableCell>
                              <TableCell>{result.data.serial_number || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Polling Station</TableCell>
                              <TableCell>{result.data.polling_station || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Address</TableCell>
                              <TableCell>{result.data.address || '–'}</TableCell>
                            </TableRow>
                            {result.data.address_line && (
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Address Line</TableCell>
                                <TableCell>{result.data.address_line}</TableCell>
                              </TableRow>
                            )}
                            {result.data.city && (
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>City</TableCell>
                                <TableCell>{result.data.city}</TableCell>
                              </TableRow>
                            )}
                            {result.data.district && (
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>District</TableCell>
                                <TableCell>{result.data.district}</TableCell>
                              </TableRow>
                            )}
                            {result.data.address_state && (
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Address State</TableCell>
                                <TableCell>{result.data.address_state}</TableCell>
                              </TableRow>
                            )}
                            {result.data.pincode && (
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Pincode</TableCell>
                                <TableCell>{result.data.pincode}</TableCell>
                              </TableRow>
                            )}
                            {result.data.country && (
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Country</TableCell>
                                <TableCell>{result.data.country}</TableCell>
                              </TableRow>
                            )}
                          </>
                        ) : serviceType.toLowerCase().includes('vehicle') || serviceType.toLowerCase().includes('rc') ? (
                          // Formatted Vehicle RC verification display
                          <>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>RC Number</TableCell>
                              <TableCell>
                                <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 700 }}>
                                  {result.data.registration_number || '–'}
                                </Typography>
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Class</TableCell>
                              <TableCell>{result.data.vehicle_class || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Chassis</TableCell>
                              <TableCell>{result.data.vehicle_chasi_number || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Engine</TableCell>
                              <TableCell>{result.data.vehicle_engine_number || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Vehicle Manufacturer</TableCell>
                              <TableCell>{result.data.maker_description || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Model</TableCell>
                              <TableCell>{result.data.maker_model || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Type</TableCell>
                              <TableCell>{result.data.fuel_type || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Norms Type</TableCell>
                              <TableCell>{result.data.norms_type || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Body Type</TableCell>
                              <TableCell>{result.data.body_type || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Owner Count</TableCell>
                              <TableCell>{result.data.owner_count || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Owner Name</TableCell>
                              <TableCell>{result.data.owner_name || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Owner's Father's Name</TableCell>
                              <TableCell>{result.data.father_name || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Mobile Number</TableCell>
                              <TableCell>{result.data.mobile_number || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Status As On</TableCell>
                              <TableCell>{result.data.status_as_on || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>RC Status</TableCell>
                              <TableCell>
                                <Box sx={{ 
                                  display: 'inline-block', 
                                  px: 2, 
                                  py: 0.5, 
                                  borderRadius: 1,
                                  bgcolor: result.data.rc_status === 'ACTIVE' ? '#4caf50' : '#f44336',
                                  color: 'white',
                                  fontWeight: 600
                                }}>
                                  {result.data.rc_status || '–'}
                                </Box>
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Reg. Authority</TableCell>
                              <TableCell>{result.data.registration_authority || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Reg. Date</TableCell>
                              <TableCell>{result.data.registration_date || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Vehicle Manufacturing Month Year</TableCell>
                              <TableCell>{result.data.manufacturing_date || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>RC Expiry Date</TableCell>
                              <TableCell>{result.data.rc_expiry_date || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Vehicle Tax Upto</TableCell>
                              <TableCell>{result.data.vehicle_tax_upto || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Vehicle Insurance Company Name</TableCell>
                              <TableCell>{result.data.insurance_company || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Vehicle Insurance Upto</TableCell>
                              <TableCell>{result.data.insurance_upto || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Vehicle Insurance Policy No.</TableCell>
                              <TableCell>{result.data.insurance_policy_number || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>RC Financer</TableCell>
                              <TableCell>{result.data.financer || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Present Address</TableCell>
                              <TableCell>{result.data.present_address || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Permanent Address</TableCell>
                              <TableCell>{result.data.permanent_address || '–'}</TableCell>
                            </TableRow>
                            {result.data.blacklist_details && 
                             result.data.blacklist_details !== '' && 
                             result.data.blacklist_details !== '[]' &&
                             !(Array.isArray(result.data.blacklist_details) && result.data.blacklist_details.length === 0) && (
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Blacklist Details</TableCell>
                                <TableCell>{typeof result.data.blacklist_details === 'object' ? JSON.stringify(result.data.blacklist_details) : result.data.blacklist_details}</TableCell>
                              </TableRow>
                            )}
                            {result.data.challan_details && 
                             result.data.challan_details !== '' && 
                             result.data.challan_details !== '[]' &&
                             !(Array.isArray(result.data.challan_details) && result.data.challan_details.length === 0) && (
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Challan Details</TableCell>
                                <TableCell>{typeof result.data.challan_details === 'object' ? JSON.stringify(result.data.challan_details) : result.data.challan_details}</TableCell>
                              </TableRow>
                            )}
                          </>
                        ) : serviceType.toLowerCase().includes('udyam') && serviceType.toLowerCase().includes('lite') ? (
                          // PAN to Udyam Lite display (third view)
                          <>
                            <TableRow>
                              <TableCell colSpan={2} sx={{ 
                                bgcolor: '#1565c0', 
                                color: 'white', 
                                fontWeight: 700, 
                                fontSize: '1.1rem',
                                textAlign: 'center',
                                py: 1.5
                              }}>
                                Udyam Registration Number : {result.data.udyam_reg_no || 'Not Found'}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>PAN</TableCell>
                              <TableCell>{result.data.pan || '–'}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>Status</TableCell>
                              <TableCell>{result.data.status || '–'}</TableCell>
                            </TableRow>
                          </>
                        ) : serviceType.toLowerCase().includes('udyam') ? (
                          // Udyam Verification display
                          <>
                            {/* Basic Info */}
                            {[
                              ['Udyam Registration Number', result.data.udyam_registration_number],
                              ['Name of Enterprise', result.data.name_of_enterprise],
                              ['Organisation Type', result.data.organisation_type],
                              ['Major Activity', result.data.major_activity],
                              ['Gender', result.data.gender],
                              ['Social Category', result.data.social_category],
                              ['Date of Incorporation', result.data.date_of_incorporation],
                              ['Date of Commencement of Production/Business', result.data.date_of_commencement],
                              ['DIC', result.data.dic],
                              ['MSME-DFO', result.data.msme_dfo],
                              ['Date of Udyam Registration', result.data.date_of_udyam_registration],
                            ].filter(([, v]) => v).map(([label, value]) => (
                              <TableRow key={label}>
                                <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>{label}</TableCell>
                                <TableCell>{String(value)}</TableCell>
                              </TableRow>
                            ))}

                            {/* Enterprise Type Table */}
                            {result.data.enterprise_type?.length > 0 && (
                              <TableRow>
                                <TableCell colSpan={2} sx={{ p: 0 }}>
                                  <Box sx={{ bgcolor: '#e3f2fd', px: 2, py: 1, fontWeight: 700 }}>Enterprise Type</Box>
                                  <Table size="small">
                                    <TableBody>
                                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                        <TableCell sx={{ fontWeight: 600 }}>S.No.</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Classification Year</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Enterprise Type</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Classification Date</TableCell>
                                      </TableRow>
                                      {result.data.enterprise_type.map((et, i) => (
                                        <TableRow key={i}>
                                          <TableCell>{et.sno}</TableCell>
                                          <TableCell>{et.classification_year}</TableCell>
                                          <TableCell>{et.enterprise_type}</TableCell>
                                          <TableCell>{et.classification_date}</TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </TableCell>
                              </TableRow>
                            )}

                            {/* Unit(s)/Plant Locations */}
                            {result.data.units?.length > 0 && (
                              <TableRow>
                                <TableCell colSpan={2} sx={{ p: 0 }}>
                                  <Box sx={{ bgcolor: '#e3f2fd', px: 2, py: 1, fontWeight: 700 }}>Unit(s)/Plant Locations Details</Box>
                                  <Table size="small">
                                    <TableBody>
                                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                        {['S.No.','Unit Name','Flat','Building','Village/Town','Block','Road','City','Pin','State','District'].map(h => (
                                          <TableCell key={h} sx={{ fontWeight: 600 }}>{h}</TableCell>
                                        ))}
                                      </TableRow>
                                      {result.data.units.map((u, i) => (
                                        <TableRow key={i}>
                                          <TableCell>{u.sno}</TableCell>
                                          <TableCell>{u.unit_name}</TableCell>
                                          <TableCell>{u.flat}</TableCell>
                                          <TableCell>{u.building}</TableCell>
                                          <TableCell>{u.village_town}</TableCell>
                                          <TableCell>{u.block}</TableCell>
                                          <TableCell>{u.road}</TableCell>
                                          <TableCell>{u.city}</TableCell>
                                          <TableCell>{u.pin}</TableCell>
                                          <TableCell>{u.state}</TableCell>
                                          <TableCell>{u.district}</TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </TableCell>
                              </TableRow>
                            )}

                            {/* Official Address */}
                            <TableRow>
                              <TableCell colSpan={2} sx={{ p: 0 }}>
                                <Box sx={{ bgcolor: '#e3f2fd', px: 2, py: 1, fontWeight: 700 }}>Official Address of Enterprise</Box>
                                <Table size="small">
                                  <TableBody>
                                    {[
                                      ['Flat/Door/Block No.', result.data.flat_door_block_no],
                                      ['Name of Premises/Building', result.data.name_of_premises],
                                      ['Village/Town', result.data.village_town],
                                      ['Block', result.data.block],
                                      ['Road/Street/Lane', result.data.road_street_lane],
                                      ['City', result.data.city],
                                      ['State', result.data.state],
                                      ['District', result.data.district],
                                      ['Pin', result.data.pin],
                                      ['Mobile', result.data.mobile],
                                      ['Email', result.data.email],
                                    ].filter(([, v]) => v).map(([label, value]) => (
                                      <TableRow key={label}>
                                        <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>{label}</TableCell>
                                        <TableCell>{String(value)}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableCell>
                            </TableRow>

                            {/* NIC Codes */}
                            {result.data.nic_codes?.length > 0 && (
                              <TableRow>
                                <TableCell colSpan={2} sx={{ p: 0 }}>
                                  <Box sx={{ bgcolor: '#e3f2fd', px: 2, py: 1, fontWeight: 700 }}>National Industry Classification Code(S)</Box>
                                  <Table size="small">
                                    <TableBody>
                                      <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                        {['S.No.','NIC 2 Digit','NIC 4 Digit','NIC 5 Digit','Activity','Date'].map(h => (
                                          <TableCell key={h} sx={{ fontWeight: 600 }}>{h}</TableCell>
                                        ))}
                                      </TableRow>
                                      {result.data.nic_codes.map((n, i) => (
                                        <TableRow key={i}>
                                          <TableCell>{n.sno}</TableCell>
                                          <TableCell>{n.nic_2_digit}</TableCell>
                                          <TableCell>{n.nic_4_digit}</TableCell>
                                          <TableCell>{n.nic_5_digit}</TableCell>
                                          <TableCell>{n.activity}</TableCell>
                                          <TableCell>{n.date}</TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </TableCell>
                              </TableRow>
                            )}
                          </>
                        ) : (
                          // Generic display for other verification types
                          Object.entries(result.data).map(([key, value]) => (
                            <TableRow key={key}>
                              <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>
                                {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </TableCell>
                              <TableCell>
                                {value === null || value === undefined
                                  ? '–'
                                  : typeof value === 'object'
                                  ? JSON.stringify(value)
                                  : String(value)}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            )}
          </Paper>
        )}

        {/* Bulk Verification Tab */}
        {activeTab === 1 && (
          <Paper sx={{ p: { xs: 2, sm: 3 } }}>
            <Grid container spacing={2} alignItems="stretch" sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} lg={3}>
                <TextField
                  fullWidth
                  select
                  label="Service Type"
                  value={serviceType}
                  onChange={(e) => setServiceType(e.target.value)}
                  size="medium"
                  sx={{ height: "100%" }}
                >
                  {serviceTypes.length === 0 ? (
                    <MenuItem value="">No services available</MenuItem>
                  ) : (
                    serviceTypes.map(type => (
                      <MenuItem key={type.id} value={type.name}>{type.name}</MenuItem>
                    ))
                  )}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6} lg={3} sx={{ display: "flex", alignItems: "stretch" }}>
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  startIcon={<CloudUpload />}
                  sx={{
                    height: "56px",
                    minHeight: "56px",
                  }}
                >
                  Choose File
                  <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    accept=".csv,.xlsx"
                    onChange={handleFileSelect}
                  />
                </Button>
              </Grid>

              <Grid item xs={12} sm={6} lg={3} sx={{ display: "flex", alignItems: "stretch" }}>
                <Button
                  variant="contained"
                  fullWidth
                  disabled={!selectedFile || uploadLoading}
                  onClick={handleBulkUpload}
                  sx={{
                    height: "56px",
                    minHeight: "56px",
                  }}
                >
                  {uploadLoading ? 'Uploading...' : 'Upload'}
                </Button>
              </Grid>

              <Grid item xs={12} sm={6} lg={3} sx={{ display: "flex", alignItems: "stretch" }}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<Download />}
                  onClick={downloadTemplate}
                  sx={{
                    height: "56px",
                    minHeight: "56px",
                  }}
                >
                  Download Template
                </Button>
              </Grid>
            </Grid>

            {selectedFile && (
              <Typography variant="caption" display="block" sx={{ mb: 2, color: "text.secondary" }}>
                Selected file: {selectedFile.name}
              </Typography>
            )}

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Box sx={{ width: "100%", overflowX: "auto" }}>
              <Table sx={{ minWidth: 900 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ bgcolor: '#1976d2', color: 'white', fontWeight: 600, py: 2, minWidth: 120 }}>Status</TableCell>
                    <TableCell sx={{ bgcolor: '#1976d2', color: 'white', fontWeight: 600, py: 2, minWidth: 150 }}>Service Type</TableCell>
                    <TableCell sx={{ bgcolor: '#1976d2', color: 'white', fontWeight: 600, py: 2, minWidth: 80 }}>Total</TableCell>
                    <TableCell sx={{ bgcolor: '#1976d2', color: 'white', fontWeight: 600, py: 2, minWidth: 80 }}>Success</TableCell>
                    <TableCell sx={{ bgcolor: '#1976d2', color: 'white', fontWeight: 600, py: 2, minWidth: 80 }}>Failed</TableCell>
                    <TableCell sx={{ bgcolor: '#1976d2', color: 'white', fontWeight: 600, py: 2, minWidth: 150 }}>Started On</TableCell>
                    <TableCell sx={{ bgcolor: '#1976d2', color: 'white', fontWeight: 600, py: 2, minWidth: 150 }}>Completed On</TableCell>
                    <TableCell sx={{ bgcolor: '#1976d2', color: 'white', fontWeight: 600, py: 2, minWidth: 120 }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {certProgress && certProgress.status && (
                    <TableRow hover>
                      <TableCell sx={{ py: 2 }}>
                        <Alert severity={certProgress.status === 'completed' ? 'success' : 'warning'} sx={{ py: 0 }}>
                          {(certProgress.status || '').toUpperCase()}
                        </Alert>
                      </TableCell>
                      <TableCell>Udyam Certificate</TableCell>
                      <TableCell>{certProgress.total}</TableCell>
                      <TableCell>{certProgress.success}</TableCell>
                      <TableCell>{certProgress.failed}</TableCell>
                      <TableCell>{certProgress.startedAt || '-'}</TableCell>
                      <TableCell>{certProgress.status === 'completed' ? certProgress.completedAt : certProgress.current ? `Downloading: ${certProgress.current}` : '-'}</TableCell>
                      <TableCell>-</TableCell>
                    </TableRow>
                  )}
                  {bulkResults.length === 0 && !certProgress ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                        No bulk verification jobs found
                      </TableCell>
                    </TableRow>
                  ) : (
                    bulkResults
                      .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                      .map((job) => {
                        const processedCount = (job.successCount || 0) + (job.failedCount || 0);
                        const progressPercent = job.totalRecords > 0 ? Math.round((processedCount / job.totalRecords) * 100) : 0;
                        
                        return (
                        <TableRow key={job.bulkJobId} hover>
                          <TableCell sx={{ py: 2 }}>
                            <Alert severity={getStatusColor(job.status)} sx={{ py: 0 }}>
                              {job.status.toUpperCase()}
                              {job.status === 'processing' && ` (${progressPercent}%)`}
                            </Alert>
                          </TableCell>
                          <TableCell sx={{ py: 2 }}>{job.verificationType}</TableCell>
                          <TableCell sx={{ py: 2 }}>{job.totalRecords}</TableCell>
                          <TableCell sx={{ py: 2, color: 'success.main', fontWeight: 600 }}>{job.successCount || 0}</TableCell>
                          <TableCell sx={{ py: 2, color: 'error.main', fontWeight: 600 }}>{job.failedCount || 0}</TableCell>
                          <TableCell sx={{ py: 2 }}>{formatDate(job.startedAt)}</TableCell>
                          <TableCell sx={{ py: 2 }}>{formatDate(job.completedAt)}</TableCell>
                          <TableCell sx={{ py: 2 }}>
                            {(job.status === 'completed' || job.status === 'cancelled') && (
                              <Button 
                                size="small" 
                                variant="outlined"
                                onClick={() => handleDownloadResults(job.bulkJobId)}
                              >
                                Download
                              </Button>
                            )}
                            {(job.status === 'pending' || job.status === 'processing') && (
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <CircularProgress size={20} />
                                <Button 
                                  size="small" 
                                  variant="outlined"
                                  color="error"
                                  onClick={() => handleCancelJob(job.bulkJobId)}
                                >
                                  Cancel
                                </Button>
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                        );
                      })
                  )}
                </TableBody>
              </Table>
            </Box>

            {/* Pagination Controls */}
            {bulkResults.length > itemsPerPage && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 3, gap: 2 }}>
                <Button
                  variant="outlined"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(prev => prev - 1)}
                >
                  Previous
                </Button>
                <Typography>
                  Page {currentPage} of {Math.ceil(bulkResults.length / itemsPerPage)}
                </Typography>
                <Button
                  variant="outlined"
                  disabled={currentPage >= Math.ceil(bulkResults.length / itemsPerPage)}
                  onClick={() => setCurrentPage(prev => prev + 1)}
                >
                  Next
                </Button>
              </Box>
            )}
          </Paper>
        )}
      </Container>

      {/* Cancel Confirmation Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={closeCancelDialog}
        aria-labelledby="cancel-dialog-title"
        aria-describedby="cancel-dialog-description"
      >
        <DialogTitle id="cancel-dialog-title">
          Cancel Bulk Verification Job
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="cancel-dialog-description">
            Are you sure you want to cancel this bulk verification job? This action cannot be undone.
            Any records that have already been processed will remain in the results.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCancelDialog} color="primary">
            No, Keep Running
          </Button>
          <Button onClick={confirmCancelJob} color="error" variant="contained" autoFocus>
            Yes, Cancel Job
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tab Switch Warning Dialog */}
      <Dialog
        open={tabSwitchDialogOpen}
        onClose={cancelTabSwitch}
        aria-labelledby="tab-switch-dialog-title"
        aria-describedby="tab-switch-dialog-description"
      >
        <DialogTitle id="tab-switch-dialog-title">
          Active Verification in Progress
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="tab-switch-dialog-description">
            {loading 
              ? 'A verification request is currently in progress. Switching tabs will cancel this request.'
              : 'You have bulk verification jobs that are currently processing. Switching tabs will not cancel them, but you may want to monitor their progress.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelTabSwitch} color="primary" variant="contained">
            Stay Here
          </Button>
          <Button onClick={confirmTabSwitch} color="warning">
            Switch Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default KYCVerification;
